import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { openConsentSettings } from './Consent';

/**
 * Rufnummer der Verwaltung. Anzeige und Wahlform getrennt: `tel:` vertraegt
 * keine Leerzeichen, im Text sollen sie aber stehen.
 */
export const TELEFON_ANZEIGE = '+49 6174 9403089';
export const TELEFON_WAHL = '+4961749403089';

/** Hoerer-Symbol fuer die Anruf-CTAs. */
export function TelefonIcon({ groesse = 15 }) {
  return (
    <svg width={groesse} height={groesse} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z" />
    </svg>
  );
}

// Kept for the (now-unused) Calendly page; CTAs no longer use it.
export const CALENDLY_URL = 'https://calendly.com/frank-balz-hambacore/15min';

/**
 * "Erstanalyse anfragen" — the single entry point for every CTA. Routes to the
 * in-house two-step /anfrage form (replaced the Calendly link, which was too
 * high-commitment for colder traffic). SubmitApplication ("form started") fires
 * on the /anfrage mount, not here, so direct visits are counted too.
 */
export function useStartAnfrage() {
  const navigate = useNavigate();
  return () => navigate('/anfrage');
}

/**
 * Schmale Leiste oberhalb der Navigation, die auf die Mietpreis-Analyse führt.
 *
 * Sie ist wie die Navigation fixiert; damit die bestehenden Freiräume aller
 * Seiten weiter stimmen, verschiebt `--bar-h` in homev2.css sowohl den
 * Seiteninhalt als auch die Navigation nach unten — die Geometrie zwischen
 * Navigation und Inhalt bleibt also unverändert.
 *
 * Sie steht auf allen Seiten, auch auf der Analyseseite selbst — dort hielt der
 * Seitenrahmen sonst 40 px fuer eine Leiste frei, die gar nicht kam.
 */
export function AnalyseLeiste() {
  return (
    <Link className="topbar" to="/kostenlose-mietpreis-analyse">
      <span className="topbar-text">
        <strong>Kostenlose Mietpreis-Analyse</strong>
      </span>
      <span className="topbar-pfeil" aria-hidden="true">→</span>
    </Link>
  );
}

export const LOGO_SRC = '/hamba-logo.png';
export const CDN_BASE_URL = (process.env.REACT_APP_CDN_BASE_URL || 'https://hamba.fra1.cdn.digitaloceanspaces.com').replace(/\/$/, '');
export const PORTRAIT_FRANK = `${CDN_BASE_URL}/FPB-Team-p-500.png`;
export const PORTRAIT_FELIX = `${CDN_BASE_URL}/FB-Team-p-500.png`;

/**
 * Observe `.homev2 .reveal` elements and fade them in when scrolled into view.
 * Pass a `deps` array to re-observe when content changes (e.g. a list re-renders
 * with a new filter). Already-revealed elements keep their `.in` class — only
 * the ones still at opacity 0 get re-observed.
 */
export function useReveal(deps = []) {
  useEffect(() => {
    const els = document.querySelectorAll('.homev2 .reveal:not(.in)');
    if (!els.length) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function useScrollNav() {
  useEffect(() => {
    const onScroll = () => {
      const nav = document.getElementById('homev2-nav');
      if (!nav) return;
      if (window.scrollY > 8) nav.classList.add('scrolled');
      else nav.classList.remove('scrolled');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
}

export function useTopProgress() {
  useEffect(() => {
    const onScroll = () => {
      const bar = document.getElementById('homev2-progress');
      if (!bar) return;
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      const pct = max > 0 ? (h.scrollTop / max) * 100 : 0;
      bar.style.width = `${pct}%`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
}

/** When the logo is clicked on the home route, scroll to top instead of re-navigating. */
function useHomeLogoHandler() {
  const location = useLocation();
  return (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
}

/**
 * Die Navigation der Website — auf jeder oeffentlichen Seite dieselbe.
 *
 * Bewusst ohne Ankerlinks in der Mitte: Die Seiten fuehren auf eine Handlung
 * hin, nicht auf ein Inhaltsverzeichnis. Links neben dem Logo steht klein der
 * Zugang fuer bestehende Kunden, rechts die zwei Wege zu uns — anrufen oder
 * anfragen.
 */
function SiteNav() {
  const onLogoClick = useHomeLogoHandler();
  const startFunnel = useStartAnfrage();
  return (
    <nav id="homev2-nav" className="nav" data-screen-label="Nav">
      <div className="nav-inner nav-inner-home">
        <div className="nav-marke">
          <Link
            to="/"
            onClick={onLogoClick}
            className="wordmark"
            aria-label="hamba"
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <img src={LOGO_SRC} alt="hamba" style={{ height: 56, width: 'auto', display: 'block' }} />
          </Link>

          <SignedIn>
            <Link to="/dashboard" className="nav-klein">Dashboard →</Link>
          </SignedIn>
          <SignedOut>
            <Link to="/login" className="nav-klein">Login</Link>
          </SignedOut>
        </div>

        <div className="nav-right">
          <a href={`tel:${TELEFON_WAHL}`} className="btn-tel"
             aria-label={`Anrufen: ${TELEFON_ANZEIGE}`}>
            <TelefonIcon />
            <span className="btn-tel-nr">{TELEFON_ANZEIGE}</span>
          </a>

          <button type="button" className="btn-cta" onClick={startFunnel}>
            Erstanalyse anfragen
          </button>
        </div>
      </div>
    </nav>
  );
}

/**
 * Anrufzeile ueber dem Kurzformular eines Aufmachers.
 *
 * Wer nicht tippen will, findet hier den kurzen Weg, bevor das Formular ihn
 * kostet. Steht auf der Startseite und auf /erstanalyse — eine Komponente,
 * damit Wortlaut und Nummer nicht auseinanderlaufen.
 */
export function AnrufZeile({ text = 'Rufen Sie uns an' }) {
  return (
    <div className="anrufzeile">
      <span className="anrufzeile-text">{text}</span>
      <a href={`tel:${TELEFON_WAHL}`} className="anrufzeile-nr">
        <TelefonIcon groesse={16} />
        {TELEFON_ANZEIGE}
      </a>
    </div>
  );
}

/**
 * Feste Aktionsleiste am unteren Rand, nur auf schmalen Schirmen.
 *
 * Auf dem Telefon ist der Daumen unten und die Navigation oben; eine CTA, die
 * mitscrollt, wird dort deutlich haeufiger benutzt als eine im Kopf. Ob sie
 * beide Wege traegt oder nur den Anruf, entscheidet die Seite (siehe unten).
 *
 * Sie muss ein direktes Kind von `.page-enter` sein — homev2.css haelt darueber
 * (`:has(> .mobilebar)`) den Fussbereich frei, damit nichts unter ihr
 * verschwindet.
 */
export function MobileAktionsleiste({ onAnfrage, label = 'Erstanalyse' }) {
  // Ohne `onAnfrage` traegt die Leiste nur den Anruf und dieser nimmt die volle
  // Breite ein — so steht sie auf /erstanalyse, wo das Formular ohnehin mehrfach
  // auf der Seite liegt. Mit `onAnfrage` teilt sie sich in zwei Haelften.
  const geteilt = typeof onAnfrage === 'function';
  return (
    <div className={`mobilebar${geteilt ? ' mobilebar--zwei' : ''}`}>
      <a href={`tel:${TELEFON_WAHL}`} className="mobilebar-tel"
         aria-label={`Anrufen: ${TELEFON_ANZEIGE}`}>
        <TelefonIcon groesse={16} />
        <span>Anrufen</span>
        {!geteilt && <span className="mobilebar-nr">{TELEFON_ANZEIGE}</span>}
      </a>
      {geteilt && (
        <button type="button" className="mobilebar-cta" onClick={onAnfrage}>
          {label}
        </button>
      )}
    </div>
  );
}

export { SiteNav };

/* Single canonical footer used on every homev2 route — home, erstanalyse,
   legal pages, login. Three columns: identity + address / contact / legal.
   No bottom row, no Erstanalyse link (CTAs live in dedicated sections). */
function SiteFooter() {
  const onLogoClick = useHomeLogoHandler();
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div>
          <Link
            to="/"
            onClick={onLogoClick}
            className="wordmark"
            aria-label="hamba"
            style={{ display: 'inline-flex', alignItems: 'center', marginBottom: 14 }}
          >
            <img src={LOGO_SRC} alt="hamba" style={{ height: 44, width: 'auto', display: 'block' }} />
          </Link>
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            hamba GmbH<br />
            Villa Regina<br />
            Limburger Str. 17<br />
            61462 Königstein im Taunus
          </div>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div style={{ color: 'var(--anthracite)', marginBottom: 6, letterSpacing: '0.02em' }}>
            Private Immobilienverwaltung · Rhein-Main
          </div>
          <div>M: <a href="mailto:contact@hambacore.com" className="footer-kontakt">contact@hambacore.com</a></div>
          <div>T: <a href={`tel:${TELEFON_WAHL}`} className="footer-kontakt">{TELEFON_ANZEIGE}</a></div>
        </div>
        <div className="footer-links">
          <Link to="/agb">AGB</Link>
          <Link to="/datenschutz">Datenschutz</Link>
          <Link to="/impressum">Impressum</Link>
          <button
            type="button"
            onClick={openConsentSettings}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              color: 'inherit',
              cursor: 'pointer',
              textAlign: 'inherit',
            }}
          >
            Cookies
          </button>
        </div>
      </div>
    </footer>
  );
}

export const HomeFooter = SiteFooter;
// MinimalFooter retained as a named export so existing callers (Legal pages,
// ErstanalysePage, SignIn) don't need to change. The `active` prop is accepted
// for backward compatibility and ignored.
export function MinimalFooter() {
  return <SiteFooter />;
}

/** Set the document.title for the current route. Pass a string and it sets <title> on mount + whenever the value changes. */
export function useDocumentTitle(title) {
  useEffect(() => {
    if (typeof title !== 'string' || title.length === 0) return;
    const previous = document.title;
    document.title = title;
    return () => {
      // Restore on unmount so the next route's title applies cleanly even if it doesn't set one immediately.
      document.title = previous;
    };
  }, [title]);
}

/** When a route mounts with a hash like `/#ansatz`, scroll to it. */
export function useScrollToHashOnMount() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location]);
}
